import React, { useRef } from 'react';
import {
  BusinessContactSection, BusinessHeaderSection, BreadCrumbSection, BusinessListItem,
} from './types';
import { LIST_SECTION_LABELS } from './constants';
import { LabelRow, SplitRow, DraftTextField, DraftTextArea } from './primitives';

interface BusinessEditorProps {
  data:     BusinessContactSection;
  onChange: (v: BusinessContactSection) => void;
}

const BusinessEditor: React.FC<BusinessEditorProps> = ({ data, onChange }) => {
  const header = data.content[0]?.headerSection;
  const orig   = useRef(data);

  const origHeader = orig.current.content[0]?.headerSection;
  const origCrumb  = orig.current.content[0]?.breadCrumbSection;

  const setHeader = (p: Partial<BusinessHeaderSection>) =>
    onChange({
      ...data,
      content: [{ ...data.content[0], headerSection: { ...header, ...p } }],
    });

  const setCrumb = (p: Partial<BreadCrumbSection>) =>
    onChange({
      ...data,
      content: [{
        ...data.content[0],
        breadCrumbSection: { ...data.content[0].breadCrumbSection, ...p },
      }],
    });

  const setListField = (idx: number, field: keyof BusinessListItem, v: string) => {
    const updatedLists = header.lists.map((item, i) =>
      i === idx ? { ...item, [field]: v } : item
    );
    setHeader({ lists: updatedLists });
  };

  if (!header || !origHeader) return null;

  return (
    <>
      <LabelRow
        label="Header section"
        toggle={{ checked: header.visible, onChange: v => setHeader({ visible: v }) }}
      />

      <LabelRow label="Main title" />
      <SplitRow currentText={origHeader.title}>
        <DraftTextField placeholder="Text title" onChange={v => setHeader({ title: v })} />
      </SplitRow>

      <LabelRow label="Title description" />
      <SplitRow currentText={origHeader.description} multiline>
        <DraftTextArea placeholder="Title description" rows={3}
          onChange={v => setHeader({ description: v })} />
      </SplitRow>

      {origHeader.lists.map((origItem, idx) => {
        const sectionLbl = LIST_SECTION_LABELS[idx] ?? `Section ${idx + 1}`;
        return (
          <React.Fragment key={origItem.id}>
            <LabelRow label={sectionLbl} />
            <SplitRow currentText={origItem.title}>
              <DraftTextField
                key={`title-${idx}`}
                placeholder="Text title"
                onChange={v => setListField(idx, 'title', v)} />
            </SplitRow>

            <LabelRow label="Secondary button" />
            <SplitRow currentText={origItem.label}>
              <DraftTextField
                key={`label-${idx}`}
                placeholder="Text button"
                onChange={v => setListField(idx, 'label', v)} />
            </SplitRow>
          </React.Fragment>
        );  
      })}

      {origCrumb && (
        <>
          <LabelRow
            label="Breadcrumb section"
            toggle={{
              checked:  data.content[0]?.breadCrumbSection?.visible ?? true,
              onChange: v => setCrumb({ visible: v }),
            }}
          />

          <LabelRow label="Breadcrumb — first title" />
          <SplitRow currentText={origCrumb.breadCrumbTitleSt}>
            <DraftTextField placeholder="e.g. Business"
              onChange={v => setCrumb({ breadCrumbTitleSt: v })} />
          </SplitRow>

          <LabelRow label="Breadcrumb — second title" />
          <SplitRow currentText={origCrumb.breadCrumbTitleNd}>
            <DraftTextField placeholder="e.g. Contact Us"
              onChange={v => setCrumb({ breadCrumbTitleNd: v })} />
          </SplitRow>
        </>
      )}
    </>
  );
};

export default BusinessEditor;